import { useQuery } from '@tanstack/react-query';

import { getKycFrozen } from 'Services/api/kyc';
import { logger } from 'Services/logger/logger';
import { MINUTE } from 'Utilities/consts';
import { isSSR } from 'Utilities/helpers';
import { getDataFromResponse } from 'Utilities/helpers/getDataFromResponse';
import useFeatureToggle, { FEATURES } from 'Utilities/hooks/useFeatureToggle';

import useUser from '../useUser';

const STALE_TIME = 5 * MINUTE;
export const KYC_FROZEN_QUERY_KEY = 'kycFrozen';

export const useIsKycFrozen = (): { isKycFrozen: boolean; isLoading: boolean } => {
	const isFeatureEnabled = useFeatureToggle(FEATURES.kycFreeze);
	const user = useUser();

	const isEnabled = !isSSR() && Boolean(isFeatureEnabled) && Boolean(user) && !user?.isLoggedOut;

	const { data, isLoading } = useQuery({
		enabled: isEnabled,
		onError: (e: unknown) => {
			const message = 'There was an issue with fetching kyc frozen status';
			const error = e instanceof Error ? e : new Error(message);
			logger.error({ context: { extra: { info: message } }, error, message, scope: 'useIsKycFrozen' });
		},
		queryFn: async ({ signal }) => {
			const { kycFrozen } = await getDataFromResponse(getKycFrozen({ config: { signal } }));
			return kycFrozen;
		},
		queryKey: [KYC_FROZEN_QUERY_KEY],
		staleTime: STALE_TIME,
	});

	return {
		isKycFrozen: Boolean(isEnabled && data),
		isLoading: isEnabled && isLoading,
	};
};
